import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, FileDown, CheckCircle2, XCircle, ArrowLeft } from "lucide-react";

export default function VisuraSuccess({ cancelled = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const sessionId = new URLSearchParams(location.search).get("session_id");
  const [phase, setPhase] = useState(cancelled ? "cancelled" : "checking"); // checking | ready | error | cancelled
  const [errorMsg, setErrorMsg] = useState("");
  const [downloading, setDownloading] = useState(false);
  const downloadedRef = useRef(false);
  const pollRef = useRef(null);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/catasto/visura/${sessionId}/document`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "visura_catastale.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download non riuscito");
    } finally { setDownloading(false); }
  };

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/catasto/visura/status/${sessionId}`);
        const { payment_status, visura_status, error } = r.data;
        if (payment_status === "paid" && visura_status === "pronta") {
          clearInterval(pollRef.current);
          setPhase("ready");
          if (!downloadedRef.current) { downloadedRef.current = true; download(); }
          return;
        }
        if (payment_status === "paid" && visura_status === "errore") {
          clearInterval(pollRef.current);
          setErrorMsg(error || "Pagamento riuscito ma generazione della visura non riuscita.");
          setPhase("error");
          return;
        }
      } catch (e) { /* continua a interrogare */ }
      if (attempts >= 30) {
        clearInterval(pollRef.current);
        setErrorMsg("Il pagamento è in elaborazione. Riprova tra poco dalla pagina Condomìni.");
        setPhase("error");
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2500);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, cancelled]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div data-testid="visura-result" className="bg-card border border-border rounded-2xl p-8 max-w-md w-full text-center">
        {phase === "checking" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto text-[#007BFF] animate-spin" />
            <h1 className="font-head text-xl font-bold mt-4">Pagamento in verifica…</h1>
            <p className="text-sm text-muted-foreground mt-2">Stiamo generando la tua visura catastale. Attendi qualche secondo.</p>
          </>
        )}
        {phase === "ready" && (
          <>
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500" />
            <h1 className="font-head text-xl font-bold mt-4">Visura pronta!</h1>
            <p className="text-sm text-muted-foreground mt-2">Il download è partito automaticamente. Se non è partito, usa il pulsante qui sotto.</p>
            <Button data-testid="visura-download-btn" onClick={download} disabled={downloading} className="mt-5 bg-[#007BFF] hover:bg-[#0056B3]">
              {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}Scarica visura PDF
            </Button>
          </>
        )}
        {phase === "error" && (
          <>
            <XCircle className="h-10 w-10 mx-auto text-red-500" />
            <h1 className="font-head text-xl font-bold mt-4">Qualcosa è andato storto</h1>
            <p className="text-sm text-muted-foreground mt-2">{errorMsg}</p>
            {sessionId && <Button data-testid="visura-retry-btn" variant="outline" onClick={download} disabled={downloading} className="mt-5">Riprova il download</Button>}
          </>
        )}
        {phase === "cancelled" && (
          <>
            <XCircle className="h-10 w-10 mx-auto text-muted-foreground" />
            <h1 className="font-head text-xl font-bold mt-4">Pagamento annullato</h1>
            <p className="text-sm text-muted-foreground mt-2">Nessun addebito effettuato. Puoi riprovare quando vuoi dal condominio.</p>
          </>
        )}
        <button data-testid="visura-back-btn" onClick={() => navigate("/condomini")} className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />Torna ai Condomìni
        </button>
      </div>
    </div>
  );
}
